/**
 * InlineSignupModal — the "just tell us who you are" modal that fires
 * ONLY when an anonymous user tries to Send a chat message on
 * DaddyTalk. Zero-friction, preserves the drafted message during
 * signup, and lands the user back where they were.
 *
 * Two auth paths:
 *   1) Magic-link email (default) — POST /auth/candidate/send-login-link
 *      → user clicks the link in email → back to the same page,
 *      logged in, with the draft still present in localStorage.
 *   2) Full email + password + name → POST /auth/candidate/register
 *      → immediate cookie login, back on the same page.
 *
 * The parent passes onAuthed() which fires after successful login so
 * the parent can immediately dispatch the pending Send action.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    X,
    Envelope,
    UserCircle,
    LockKey,
    PaperPlaneTilt,
    ChatCircleText,
    ShieldCheck,
} from "@phosphor-icons/react";

export default function InlineSignupModal({
    open,
    onClose,
    onAuthed,               // callback fired after successful signup/login
    contextLine,            // e.g. "So Priya can reply to your message."
    preservedDraft,         // the message the user typed (shown as reassurance)
}) {
    const [mode, setMode] = useState("magic"); // "magic" | "password"
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [sentLink, setSentLink] = useState(false);

    if (!open) return null;

    const submitMagic = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post("/auth/candidate/send-login-link", {
                email: email.trim().toLowerCase(),
            });
            setSentLink(true);
            toast.success("Magic link sent — check your inbox.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't send the link. Try again in a bit.",
            );
        } finally {
            setBusy(false);
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        if (!email.trim() || !name.trim() || password.length < 8) {
            toast.error("Fill name, email, and a password of at least 8 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/candidate/register", {
                email: email.trim().toLowerCase(),
                name: name.trim(),
                password,
                accept_terms: true,
                accepted_terms_version: "v1",
                accepted_privacy_version: "v1",
            });
            toast.success("Welcome to NaukriDaddy!");
            onAuthed?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === "string" && /already registered/i.test(detail)) {
                toast.error("Email already in use — try signing in with the magic link instead.");
                setMode("magic");
            } else {
                toast.error(detail || "Couldn't create your account. Try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[110] bg-ink/70 flex items-center justify-center p-4"
            data-testid="daddytalk-signup-modal"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div className="bg-white w-full max-w-md border border-ink/10 shadow-2xl relative">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-3">
                    <div className="flex items-center gap-3">
                        <img
                            src="/mascot/daddytalk/avatar_hello.png"
                            alt=""
                            className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                        />
                        <div>
                            <h2 className="font-heading text-lg font-black">
                                Just tell us who you are
                            </h2>
                            <p className="text-xs text-ink/60 mt-0.5">
                                {contextLine || "So the recruiter can reply to you."}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="daddytalk-signup-close"
                        aria-label="Close"
                        className="text-ink/50 hover:text-ink"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {/* Preserved draft reassurance */}
                {preservedDraft ? (
                    <div className="mx-6 my-2 px-3 py-2 bg-cobalt/5 border border-cobalt/20 text-xs text-ink/70 flex items-start gap-2">
                        <ChatCircleText size={14} weight="bold" className="text-cobalt shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <p className="font-semibold text-ink mb-0.5">Your message is saved</p>
                            <p className="line-clamp-2 italic">"{preservedDraft}"</p>
                        </div>
                    </div>
                ) : null}

                {/* Mode tabs */}
                <div className="flex border-b border-ink/10 mx-6 mt-3">
                    {[
                        { id: "magic", label: "Magic link", icon: Envelope, testid: "daddytalk-signup-tab-magic" },
                        { id: "password", label: "Email + password", icon: LockKey, testid: "daddytalk-signup-tab-password" },
                    ].map(({ id, label, icon: Icon, testid }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setMode(id)}
                            data-testid={testid}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-black uppercase tracking-wider transition-colors ${
                                mode === id
                                    ? "text-cobalt border-b-2 border-cobalt -mb-px"
                                    : "text-ink/50 hover:text-ink"
                            }`}
                        >
                            <Icon size={12} weight="bold" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    {mode === "magic" ? (
                        sentLink ? (
                            <div
                                className="text-center py-4"
                                data-testid="daddytalk-signup-magic-sent"
                            >
                                <img
                                    src="/mascot/daddytalk/avatar_celebrating.png"
                                    alt=""
                                    className="w-20 h-20 rounded-full mx-auto mb-3 shadow-md"
                                />
                                <h3 className="font-heading font-bold text-base mb-1">
                                    Check your email
                                </h3>
                                <p className="text-xs text-ink/60 mb-4">
                                    We sent a magic link to <strong>{email}</strong>. Click it to
                                    finish signing in — your message will send automatically.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => { setSentLink(false); setEmail(""); }}
                                    className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                                >
                                    Use a different email
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submitMagic} className="space-y-3">
                                <label className="block">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                        Your email
                                    </span>
                                    <input
                                        type="email"
                                        autoFocus
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="daddytalk-signup-email-magic"
                                        placeholder="you@example.com"
                                        className="w-full px-3 py-2.5 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                    />
                                </label>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="daddytalk-signup-magic-submit"
                                    className="w-full btn-gradient inline-flex items-center justify-center gap-2 py-3 text-[11px] font-black uppercase tracking-[0.2em] disabled:opacity-60"
                                >
                                    {busy ? "Sending…" : "Send magic link"}
                                    <PaperPlaneTilt size={12} weight="bold" />
                                </button>
                                <p className="text-[10px] text-ink/50 text-center">
                                    No password to remember. We'll email you a one-tap sign-in link.
                                </p>
                            </form>
                        )
                    ) : (
                        <form onSubmit={submitPassword} className="space-y-3">
                            <label className="block">
                                <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                    Your name
                                </span>
                                <div className="relative">
                                    <UserCircle
                                        size={14}
                                        weight="bold"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"
                                    />
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="daddytalk-signup-name"
                                        placeholder="Priya Sharma"
                                        className="w-full pl-9 pr-3 py-2.5 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                    Email
                                </span>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="daddytalk-signup-email-password"
                                    placeholder="you@example.com"
                                    className="w-full px-3 py-2.5 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1 block">
                                    Password (min 8 chars)
                                </span>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="daddytalk-signup-password"
                                    placeholder="••••••••"
                                    className="w-full px-3 py-2.5 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="daddytalk-signup-password-submit"
                                className="w-full btn-gradient inline-flex items-center justify-center gap-2 py-3 text-[11px] font-black uppercase tracking-[0.2em] disabled:opacity-60"
                            >
                                {busy ? "Creating…" : "Create account & send"}
                                <PaperPlaneTilt size={12} weight="bold" />
                            </button>
                        </form>
                    )}
                </div>

                {/* Footer trust line */}
                <div className="px-6 pb-5 pt-1 border-t border-ink/5 flex items-center gap-2 text-[10px] text-ink/50">
                    <ShieldCheck size={12} weight="bold" className="text-emerald-500 shrink-0" />
                    <span>
                        DPDPA-compliant · your data is never shared without consent · unsubscribe anytime
                    </span>
                </div>
            </div>
        </div>
    );
}
